import { Button, Flex, Text, VStack, useDisclosure } from "@chakra-ui/react";
import { useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { RiArrowDownSLine, RiArrowUpSLine } from "react-icons/ri";
import { GenericError } from "src/components/Feedback/GenericError";
import { Loading } from "src/components/Feedback/Loading";
import { SearchInput } from "src/components/Inputs/SearchInput";
import { RemoteDataView } from "src/components/Layout/RemoteDataView";
import { Glossary } from "src/components/Text/Glossary";
import * as FSQ from "src/generated/hooks/form-service/queries";
import { useAvelaToast } from "src/hooks/useAvelaToast";
import { useDeleteConfirmationDialog } from "src/hooks/useConfirmationDialog";
import { useGlossary } from "src/hooks/useGlossary";
import { useOrganization } from "src/hooks/useOrganization";
import { maybePluralize } from "src/services/format";
import * as RD from "src/types/remoteData";
import { useEnrollmentPeriodContext } from "../../Edit";
import { CriteriaSettingsDrawer } from "../eligibilityConfiguration/CriteriaSettingsDrawer";
import {
  criterionForBin,
  criterionForSchool,
  type CriterionForEntity,
} from "../eligibilityConfiguration/criterionForEntity";
import { flattenOfferingOptions, groupOfferingsBySchoolId } from "./offerings";
import { SchoolOfferingsCard } from "./SchoolOfferingsCard";

type Props = {
  onGoToOfferingCategories: () => void;
};

export const OfferingsPanel: React.FC<Props> = ({
  onGoToOfferingCategories,
}) => {
  const organization = useOrganization();
  const { enrollmentPeriod } = useEnrollmentPeriodContext();
  const { glossary } = useGlossary();
  const toast = useAvelaToast();
  const queryClient = useQueryClient();
  const showSeatTypes = enrollmentPeriod.use_service_offering_seat_types;
  const {
    confirm: confirmDeleteOffering,
    confirmationDialog: deleteOfferingDialog,
  } = useDeleteConfirmationDialog({
    header: "Delete offering",
    body: "Are you sure you want to delete this offering? You can't undo this action afterwards.",
  });
  const {
    confirm: confirmDeleteSeatType,
    confirmationDialog: deleteSeatTypeDialog,
  } = useDeleteConfirmationDialog({
    header: "Delete seat type",
    body: "Are you sure you want to delete this seat type? You can't undo this action afterwards.",
  });
  const [searchQuery, setSearchQuery] = useState("");
  const [expandedSchoolIds, setExpandedSchoolIds] = useState<Set<string>>(
    () => new Set()
  );
  const [deletingOfferingId, setDeletingOfferingId] = useState<string | null>(
    null
  );
  const [deletingSeatTypeId, setDeletingSeatTypeId] = useState<string | null>(
    null
  );
  const [criterionForEntity, setCriterionForEntity] =
    useState<CriterionForEntity | null>(null);
  const criteriaSettings = useDisclosure();
  const organizationId = organization.map((org) => org.id).withDefault("");

  const schoolsQuery = FSQ.useGetSchools(
    {
      query: {
        organizationId,
        status: "Active",
      },
    },
    [organizationId],
    { enabled: !!organizationId }
  );
  const offeringsQuery = FSQ.useGetOfferings(
    {
      path: {
        enrollmentPeriodId: enrollmentPeriod.id,
      },
    },
    [enrollmentPeriod.id]
  );
  const categoriesQuery = FSQ.useGetOfferingCategories(
    {
      path: {
        enrollmentPeriodId: enrollmentPeriod.id,
      },
    },
    [enrollmentPeriod.id]
  );
  const { mutateAsync: deleteOffering } = FSQ.useDeleteOffering();
  const { mutateAsync: createOfferings } = FSQ.useCreateOfferings();
  const { mutateAsync: addOfferingSeatTypes } = FSQ.useAddOfferingSeatTypes();
  const { mutateAsync: deleteOfferingSeatType } =
    FSQ.useDeleteOfferingSeatType();

  const schoolsRD = RD.useFromRestResultMemoized(schoolsQuery).map(
    (response) => {
      const schools = Array.isArray(response?.data) ? response.data : [];
      return [...schools].sort((a, b) =>
        a.name.toLowerCase().localeCompare(b.name.toLowerCase())
      );
    }
  );
  const offeringsRD = RD.useFromRestResultMemoized(offeringsQuery).map(
    (response) => (Array.isArray(response?.data) ? response.data : [])
  );
  const categoriesRD = RD.useFromRestResultMemoized(categoriesQuery).map(
    (response) => (Array.isArray(response?.data) ? response.data : [])
  );
  const remoteData = RD.toTuple3(schoolsRD, offeringsRD, categoriesRD);

  const invalidateOfferings = () =>
    queryClient.invalidateQueries({
      queryKey: [FSQ.useGetOfferingsKey],
      exact: false,
    });

  const handleDeleteOffering = async (offeringId: string) => {
    const confirmed = await confirmDeleteOffering();
    if (!confirmed) {
      return;
    }

    setDeletingOfferingId(offeringId);
    try {
      await deleteOffering({
        path: {
          enrollmentPeriodId: enrollmentPeriod.id,
          offeringId,
        },
      });
      await invalidateOfferings();
      toast({ title: "Offering deleted", status: "info" });
    } catch (error) {
      console.error(error);
      toast.error({ title: "Unable to delete offering" });
    } finally {
      setDeletingOfferingId(null);
    }
  };

  const handleAddOffering = async (
    schoolId: string,
    offeringOptionIds: string[]
  ) => {
    try {
      await createOfferings({
        path: {
          enrollmentPeriodId: enrollmentPeriod.id,
        },
        body: [
          {
            schoolId,
            offeringOptionIds,
          },
        ],
      });
      await invalidateOfferings();
      toast({ title: "Offering added", status: "info" });
    } catch (error) {
      console.error(error);
      toast.error({ title: "Unable to add offering" });
    }
  };

  const handleAddSeatType = async (
    offeringId: string,
    optionId: string | null
  ) => {
    try {
      await addOfferingSeatTypes({
        path: {
          enrollmentPeriodId: enrollmentPeriod.id,
          offeringId,
        },
        body: [{ offeringOptionIds: optionId ? [optionId] : [] }],
      });
      await invalidateOfferings();
      toast({ title: "Seat type added", status: "info" });
    } catch (error) {
      console.error(error);
      toast.error({ title: "Unable to add seat type" });
    }
  };

  const handleDeleteSeatType = async (
    offeringId: string,
    seatTypeId: string
  ) => {
    const confirmed = await confirmDeleteSeatType();
    if (!confirmed) {
      return;
    }

    setDeletingSeatTypeId(seatTypeId);
    try {
      await deleteOfferingSeatType({
        path: {
          enrollmentPeriodId: enrollmentPeriod.id,
          offeringId,
          seatTypeId,
        },
      });
      await invalidateOfferings();
      toast({ title: "Seat type deleted", status: "info" });
    } catch (error) {
      console.error(error);
      toast.error({ title: "Unable to delete seat type" });
    } finally {
      setDeletingSeatTypeId(null);
    }
  };

  if (!organization.hasData()) {
    return null;
  }

  return (
    <>
      <RemoteDataView
        remoteData={remoteData}
        loading={<Loading />}
        error={(error) => <GenericError error={error as Error} />}
      >
        {([schools, offerings, categories]) => {
          if (schools.length === 0) {
            return (
              <Text color="blackAlpha.700">
                <Glossary>You have not uploaded any schools</Glossary>
              </Text>
            );
          }

          const optionItems = flattenOfferingOptions(categories);
          const offeringsBySchoolId = groupOfferingsBySchoolId(
            offerings,
            optionItems
          );

          const filteredSchools = searchQuery.trim()
            ? schools.filter((school) =>
                school.name
                  .toLowerCase()
                  .includes(searchQuery.toLowerCase().trim())
              )
            : schools;

          const allExpanded =
            filteredSchools.length > 0 &&
            filteredSchools.every((school) => expandedSchoolIds.has(school.id));

          return (
            <VStack mt={2} gap={6} align="stretch">
              <SearchInput
                placeholder={glossary`Search enrollment schools`}
                value={searchQuery}
                onSearchChange={setSearchQuery}
                onClear={() => setSearchQuery("")}
                variant="leftIcon"
              />
              <VStack gap={4} align="stretch">
                <Flex align="center" justify="space-between">
                  <Text fontSize="xs" fontWeight="bold">
                    {filteredSchools.length}{" "}
                    {glossary`${maybePluralize(
                      filteredSchools.length,
                      "school"
                    )}`}
                  </Text>
                  <Button
                    size="xs"
                    variant="link"
                    colorScheme="primary"
                    display="inline-flex"
                    alignItems="center"
                    leftIcon={
                      allExpanded ? <RiArrowUpSLine /> : <RiArrowDownSLine />
                    }
                    onClick={() => {
                      const shownSchoolIds = filteredSchools.map(
                        (school) => school.id
                      );
                      setExpandedSchoolIds((current) => {
                        const next = new Set(current);
                        if (allExpanded) {
                          shownSchoolIds.forEach((id) => next.delete(id));
                        } else {
                          shownSchoolIds.forEach((id) => next.add(id));
                        }
                        return next;
                      });
                    }}
                  >
                    {allExpanded ? "Collapse all" : "Expand all"}
                  </Button>
                </Flex>
                {filteredSchools.map((school) => (
                  <SchoolOfferingsCard
                    key={school.id}
                    schoolId={school.id}
                    schoolName={school.name}
                    offerings={offeringsBySchoolId[school.id] ?? []}
                    options={optionItems}
                    isExpanded={expandedSchoolIds.has(school.id)}
                    showSeatTypes={showSeatTypes}
                    deletingOfferingId={deletingOfferingId}
                    deletingSeatTypeId={deletingSeatTypeId}
                    onDeleteOffering={handleDeleteOffering}
                    onDeleteSeatType={handleDeleteSeatType}
                    onAddOffering={(optionIds) =>
                      handleAddOffering(school.id, optionIds)
                    }
                    onAddSeatType={handleAddSeatType}
                    onGoToOfferingCategories={onGoToOfferingCategories}
                    onOpenSchoolCriteriaSettings={() => {
                      setCriterionForEntity(
                        criterionForSchool({
                          enrollmentPeriodId: enrollmentPeriod.id,
                          schoolId: school.id,
                          schoolName: school.name,
                          useSeatTypes: showSeatTypes,
                        })
                      );
                      criteriaSettings.onOpen();
                    }}
                    onOpenBinCriteriaSettings={(bin) => {
                      setCriterionForEntity(
                        criterionForBin({
                          enrollmentPeriodId: enrollmentPeriod.id,
                          binId: bin.id,
                          binLabel: bin.label,
                          school: bin.school,
                          offeringOptions: bin.offeringOptions,
                          binOption: bin.binOption,
                        })
                      );
                      criteriaSettings.onOpen();
                    }}
                    onToggleExpanded={() => {
                      setExpandedSchoolIds((current) => {
                        const next = new Set(current);
                        if (next.has(school.id)) {
                          next.delete(school.id);
                        } else {
                          next.add(school.id);
                        }
                        return next;
                      });
                    }}
                  />
                ))}
              </VStack>
            </VStack>
          );
        }}
      </RemoteDataView>
      {deleteOfferingDialog}
      {deleteSeatTypeDialog}
      {criterionForEntity && (
        <CriteriaSettingsDrawer
          enrollmentPeriodId={enrollmentPeriod.id}
          criterionForEntity={criterionForEntity}
          isOpen={criteriaSettings.isOpen}
          onClose={criteriaSettings.onClose}
        />
      )}
    </>
  );
};
